import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { routes } from '@/app/routes'
import { authDestination, destinationPath, homeFor, safeNextPath } from '@/features/auth/auth-destination'
import { useUser } from '@/features/auth/useSession'
import type { ProfileType } from '@/types/models'

// Layout-route guards: they decide where someone belongs; the destination rule lives in auth-destination.ts.

/** Login and verification: a signed-in person is sent where they belong (honouring ?next=). */
export function RequireGuest() {
  const user = useUser()
  const location = useLocation()
  if (!user) return <Outlet />
  const next = safeNextPath(new URLSearchParams(location.search).get('next'))
  const destination = authDestination(user)
  return <Navigate to={destination === 'completeProfile' || !next ? destinationPath(destination) : next} replace />
}

export function RequireAuth() {
  const user = useUser()
  const location = useLocation()
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`${routes.login}?next=${next}`} replace />
  }
  if (!user.profileCompleted) return <Navigate to={routes.completeProfile} replace />
  return <Outlet />
}

export function RequireIncompleteProfile() {
  const user = useUser()
  if (!user) return <Navigate to={routes.login} replace />
  if (user.profileCompleted) return <Navigate to={homeFor(user.defaultProfile)} replace />
  return <Outlet />
}

// The wrong role goes to its own home, not a 403: one account switches roles in Settings and stale links are common.
export function RequireRole({ role }: { role: ProfileType }) {
  const user = useUser()
  if (!user) return <Navigate to={routes.login} replace />
  if (user.defaultProfile !== role) return <Navigate to={homeFor(user.defaultProfile)} replace />
  return <Outlet />
}

export function RootRedirect() {
  const user = useUser()
  return <Navigate to={destinationPath(authDestination(user))} replace />
}
